import { gql, useQuery } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import useCheckPC from "@repo/hooks/utils/useCheckPC";
import type { IProductDetailQuery } from "@repo/shopify/src/queries/product-gql";
import { productDetailQuery } from "@repo/shopify/src/queries/product-gql";
import { extractId } from "@repo/shopify/src/utils";
import { checkMembershipAccessible } from "@repo/shopify/src/utils/membership";
import type { Product } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Loading from "@components/molecules/Loading";

import Breadcrumb from "@molecules/Breadcrumb";
import ImageSlider from "@molecules/ImageSlider";

import PreOrderDetail from "@organisms/PreOrderDetail";
import ProductDetail from "@organisms/ProductDetail";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";
import { getAvailableSize } from "@utils/product";

import { fetchVariantBackup } from "@api/lamdaApi";

export default function Products({ isSaleShutDown }: { isSaleShutDown: boolean }) {
  const navigate = useNavigate();
  const { handle } = useParams();

  const { isPC } = useCheckPC();
  const { setMetaTag } = useMeta();
  const { openToast, showFooter } = useLayout();
  const { customer, accessToken } = useUser();

  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [size, setSize] = useState<{ value: string; name: string }[]>([]);
  const [product, setProduct] = useState<IProductDetailQuery["product"] | null>(null);

  // 상품 조회
  useQuery<IProductDetailQuery>(gql(productDetailQuery.query), {
    variables: { handle },
    onCompleted: (_data) => {
      if (!_data.product) {
        navigate(URLS.NOT_FOUND);
        return;
      }

      // 멤버십 유효성 확인
      const isMembershipAccessible = checkMembershipAccessible(_data.product as Product, customer);
      if (!isMembershipAccessible) {
        navigate(URLS.HOME);
        if (accessToken) {
          openToast(NOTIFICATIONS.WARN_NO_ACCESS);
        } else {
          openToast(NOTIFICATIONS.WARN_REQUEST_LOGIN);
          // openLoginModal({ redirectUrl: pathname });
        }
      }

      // 상품에 `PreSale` 태그가 있으면 고객에게도 `PreSale` 태그가 있을 때만 상품 페이지 노출
      const isPreSale = _data.product.tags.includes("PreSale") && !customer.tags.includes("PreSale");

      // Hidden "true"
      const isHidden =
        _data.product.hidden?.value === "true" &&
        !customer.tags.includes("Staff") &&
        !customer.tags.includes("Employee") &&
        // PreSale이 아니면 안보이기
        !_data.product.tags.includes("PreSale") &&
        !customer.tags.includes("PreSale");

      // draw 상품
      const isDraw = _data.product.tags.includes("Draw") && !_data.product.tags.includes("OnSale");

      if (isPreSale || isHidden || isDraw) {
        setIsAvailable(false);
        navigate(isDraw ? `${URLS.DRAW}/${_data.product.handle}` : URLS.NOT_FOUND);
        return;
      }

      // 시즌 오프 중 카트 할인 적용 상품일 때 안내 토스트 노출
      if (_data.product.tags.includes("Seasonoff10")) {
        openToast(NOTIFICATIONS.INFO_SALE_10);
        return;
      }

      setProduct(_data?.product);
      setMetaTag({
        title: _data.product.title
          ? `TUNE.KR - ${_data.product.vendor.toUpperCase()} ${_data.product.title}`
          : "TUNE.KR",
        imageSrc: _data.product.variants.edges[0].node.image?.url || "",
      });
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][ProductDetail] Failed to query", error);
      throw new Error(`[SHOPIFY][ProductDetail] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  useEffect(() => {
    showFooter(true);
  }, []);

  useEffect(() => {
    setIsLoading(true);
  }, [handle]);

  const breadCrumbs = (() => {
    if (!product) return [];

    const crumbs = [];
    crumbs.push({
      name: product.productType,
      link: `${URLS.COLLECTIONS}/${product.productType.trim().toLowerCase().replace(/\W+/g, "-")}-archive-sale`,
    });

    product.category &&
      crumbs.push({
        name: product.category.value,
        link: `${URLS.COLLECTIONS}/${product.category.value.trim().toLowerCase().replace(/\W+/g, "-")}-archive-sale`,
      });
    return crumbs;
  })();

  const images = useMemo(() => (product ? product.images.edges.map(({ node }) => node.url) : []), [product]);

  const isPreOrder = useMemo(() => product?.tags.includes("Preorder") || false, [product]);

  useEffect(() => {
    if (product) void getSize();
  }, [product]);

  const getSize = async () => {
    if (!product) return;

    setIsLoading(true);

    const sizeList = getAvailableSize(product);

    const keyList = sizeList.map((item) => ({
      variantId: { N: extractId(item.value) },
    }));

    if (!keyList.length) {
      setIsLoading(false);
      return;
    }

    const result = await fetchVariantBackup(keyList);

    if (result) {
      const stockData: Record<string, number> = {};
      sizeList.forEach((size) => {
        const dbData = result.find((item) => extractId(size.value) === item.variantId.N);
        const dbQuantity = Math.max(parseFloat(dbData?.quantity.N || dbData?.quantity.S || "0"), 0);
        const originQuantity = size?.quantity || 0; // storefront에서 기져오는 갯수

        if (originQuantity !== dbQuantity) {
          console.log(`[TUNE] Variants Quantity is Different - Storefront: ${size.quantity} / DB: ${dbQuantity} `);
        }

        if (dbData) {
          stockData[dbData.variantId.N] = dbQuantity ?? originQuantity;
        }
      });

      const resultList = sizeList.filter((size) => {
        const id = extractId(size.value);
        return stockData[id] && stockData[id] > 0;
      });

      setSize(resultList);
    } else {
      const resultList = product.variants.edges.map(({ node }) => ({
        value: node.id,
        name: node.selectedOptions.find((item) => item.name === "Size")?.value || "",
        quantity: node.quantityAvailable,
      }));
      setSize(resultList);
    }

    setIsLoading(false);
  };

  if (!isAvailable || isLoading) return <Loading />;

  return (
    <>
      {product ? (
        <>
          {isPreOrder ? (
            <PreOrderDetail product={product} />
          ) : (
            <section className="pc:col-span-12 pc:mt-5 pc:grid-cols-2 col-span-6 grid grid-cols-1 gap-5">
              {!isPC ? <Breadcrumb menus={breadCrumbs} /> : null}
              <section className="flex flex-col gap-5">
                {isPC ? <Breadcrumb menus={breadCrumbs} /> : null}
                <ProductDetail isSaleShutDown={isSaleShutDown} product={product} size={size} />
              </section>
              <ImageSlider images={images} />
            </section>
          )}
        </>
      ) : null}
    </>
  );
}
